import styled, { css, keyframes } from "styled-components";

const attackAnimation = keyframes`
 0% { transform: translateX(0); }
 50% { transform: translateX(25px); }
 100% { transform: translateX(0); }
`;

const receiveDamageAnimation = keyframes`
 0% { transform: scale(1); }
 50% { transform: scale(0.9); }
 100% { transform: scale(1); }
`;

// Gunshot Animations
const gunShotAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(20px);
    opacity: 0.5;
  }
  100% {
    transform: translateX(40px);
    opacity: 0;
  }
`;

const muzzleFlashAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

// Melee Attack Animations
const meleeAttackAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(20px) rotate(20deg);
  }
  100% {
    transform: translateX(0) rotate(0);
  }
`;

const hitParticlesAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

// Dodge/Parry Animations
const dodgeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-20px) rotate(-10deg);
  }
  100% {
    transform: translateX(0) rotate(0);
  }
`;

// Reloading Animations
const reloadingAnimation = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
  100% {
    transform: translateY(0);
  }
`;

// Health Depletion Animations
const healthBurnAnimation = keyframes`
  0% {
    background-color: red;
  }
  100% {
    background-color: transparent;
  }
`;

const bloodDripAnimation = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(20px);
    opacity: 0;
  }
`;

// Environmental Animations
const tumbleweedAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const flickeringLightAnimation = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

// Ability Animations
const healingGlowAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 20px 10px rgba(0, 255, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 0, 0);
  }
`;

const attackAuraAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 20px 10px rgba(255, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
`;

// Defeat Animations
const defeatAnimation = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(20px);
  }
  100% {
    transform: translateY(0);
    opacity: 0;
  }
`;

const defeatFadeAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const PlayerHealth = styled.div`
  padding: 10px;
  background-color: #f8f8f8;
  max-height: 1rem;
  border-radius: 5px;
  position: relative; // For DamageText positioning
  ${(props) =>
  props.animate === "playerAttack" &&
  css`
      animation: ${attackAnimation} 0.2s ease-in-out;
    `}
  ${(props) =>
  props.animate === "playerDamage" &&
  css`
      animation: ${receiveDamageAnimation} 0.2s ease-in-out;
    `}
    ${(props) =>
  props.animate === "gunshot" &&
  css`
      animation: ${gunShotAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "muzzleFlash" &&
  css`
      animation: ${muzzleFlashAnimation} 0.2s ease-in-out;
    `}

  ${(props) =>
  props.animate === "meleeAttack" &&
  css`
      animation: ${meleeAttackAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "hitParticles" &&
  css`
      animation: ${hitParticlesAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "dodge" &&
  css`
      animation: ${dodgeAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "reloading" &&
  css`
      animation: ${reloadingAnimation} 1s ease-in-out;
    `}

  ${(props) =>
  props.animate === "healthBurn" &&
  css`
      animation: ${healthBurnAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "bloodDrip" &&
  css`
      animation: ${bloodDripAnimation} 1s ease-in-out;
    `}

  ${(props) =>
  props.animate === "tumbleweed" &&
  css`
      animation: ${tumbleweedAnimation} 5s linear infinite;
    `}

  ${(props) =>
  props.animate === "flickeringLight" &&
  css`
      animation: ${flickeringLightAnimation} 2s ease-in-out infinite;
    `}

  ${(props) =>
  props.animate === "healingGlow" &&
  css`
      animation: ${healingGlowAnimation} 1s ease-in-out;
    `}

  ${(props) =>
  props.animate === "attackAura" &&
  css`
      animation: ${attackAuraAnimation} 0.5s ease-in-out;
    `}

  ${(props) =>
  props.animate === "defeat" &&
  css`
      animation: ${defeatAnimation} 2s ease-in-out;
    `}

  ${(props) =>
  props.animate === "defeatFade" &&
  css`
      animation: ${defeatFadeAnimation} 2s ease-in-out;
    `}
`;

const EnemyHealth = styled.div`
  background-color: #d3d3d3;
  margin-bottom: 0.1rem;
  padding: 0.5rem;
  ${(props) =>
  props.animate === "enemyAttack" &&
  css`
      animation: ${attackAnimation} 0.2s ease-in-out;
    `}
  ${(props) =>
  props.animate === "enemyDamage" &&
  css`
      animation: ${receiveDamageAnimation} 0.2s ease-in-out;
    `}
    & button {
    margin-right: 1rem;
  }
`;

const floatAwayAnimation = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(-30px); opacity: 0; }
`;

// Add the floatAwayAnimation to styled-components
const DamageText = styled.span`
  animation: ${floatAwayAnimation} 0.8s ease forwards;
  color: red; // Or any color that fits your design
  position: absolute; // Adjust positioning as needed
`;

const Combatants = styled.div`
  display: flex; /* Arrange elements horizontally */
  justify-content: space-between; /* Distribute elements evenly */
  align-items: center; /* Center elements vertically */
`;

const CombatContainer = styled.div`
  /* Additional container styling */
`;

export { Combatants, CombatContainer, DamageText, EnemyHealth, PlayerHealth };
